import { useAppDispatch, useAppSelector } from "ui/hook/store";
import {
  HiArrowLeft,
  HiDownload,
  HiHeart,
  HiLink,
  HiOutlineHeart,
} from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { translate } from "ui/i18n";
import { useWindowWidth } from "ui/utils/hooks";
import { toggleActivityFavorite } from "ui/utils/toggle-activity-favorite";
import { useEffect, useState } from "react";
import { selectVisualSupport } from "domain/visual-supports/visual-supports.selectors";
import { getVisualSupport } from "domain/visual-supports/visual-supports.actions";
import { VisualSupportBanner } from "ui/pages/app/visual-supports/visual-support/VisualSupportBanner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from "@chakra-ui/react";
import { Snackbar } from "ui/pages/snackbar";
import { selectIsPremium } from "domain/auth/auth.selectors";
import { Link } from "react-router-dom";
import { selectFavoriteVisualSupports } from "domain/activities/activities.selectors";
import { Advice } from "ui/pages/app/components/activities/Advice";
import { getDownloadLink } from "domain/activities/activities.actions";

export function VisualSupport({
  selectedSupportId,
}: {
  selectedSupportId: string | null;
}) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isDesktop = useWindowWidth();
  const support = useAppSelector(selectVisualSupport);
  const favoriteSupport = useAppSelector(selectFavoriteVisualSupports).find(
    (t) => t.visuelId === support?.id
  );
  const [isLoading, setIsLoading] = useState(true);
  const popoverState = useDisclosure();
  const isPremium = useAppSelector(selectIsPremium);

  useEffect(() => {
    if (selectedSupportId) {
      fetchInfos();
    }
  }, [selectedSupportId]); //eslint-disable-line

  const fetchInfos = async () => {
    const { success, support } = await dispatch(
      getVisualSupport(Number(selectedSupportId))
    ).unwrap();
    if (!success || (support?.premium && !isPremium)) {
      navigate("/visual-supports");
    }
    setIsLoading(false);
  };

  const handleFavoriteClick = async (e: any) => {
    e.stopPropagation();
    toggleActivityFavorite({
      favoriteActivity: favoriteSupport,
      dispatch,
      activityId: support.id,
      activityType: "visual-support",
    });
  };

  const handleDownloadClick = async (e: any, type: "pdf" | "png") => {
    e.stopPropagation();
    if (!isPremium && !!support.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "visual-support"
      );
    } else {
      const result = await dispatch(
        getDownloadLink({
          type: "visual",
          id: support.id,
          property: type,
        })
      ).unwrap();
      if (result.success && result.url) {
        window.open(result.url, "_blank").focus();
      }
    }
  };

  return (
    <div className="flex h-full flex-col bg-darkGrey-100 p-4 md:flex-row md:py-8 md:pl-6 md:pr-10">
      <div className="mb-4 flex w-full items-center justify-between md:my-0 md:w-12 md:items-start">
        <button
          onClick={() => navigate(-1)}
          className="z-50 flex h-5 items-center"
        >
          <HiArrowLeft size={24} className="text-darkGrey-800" />
        </button>
        {!isDesktop && (
          <div className="flex w-fit space-x-4">
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={handleFavoriteClick}
            >
              {!!favoriteSupport?.id ? (
                <HiHeart className="text-secondaryGrey-900" size={20} />
              ) : (
                <HiOutlineHeart className="text-secondaryGrey-900" size={20} />
              )}
            </button>

            <Popover
              isOpen={popoverState.isOpen}
              onOpen={popoverState.onOpen}
              onClose={popoverState.onClose}
              placement="bottom-start"
            >
              <PopoverTrigger>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    popoverState.isOpen ? "bg-darkGrey-200" : "bg-white"
                  }`}
                >
                  <HiDownload
                    size={20}
                    className={`${
                      popoverState.isOpen
                        ? "text-darkGrey-700"
                        : "text-secondaryGrey-900"
                    }`}
                  />
                </button>
              </PopoverTrigger>
              <PopoverContent
                style={{
                  isolation: "isolate",
                  zIndex: 100,
                  width: "fit-content",
                  border: "none",
                  backgroundColor: "transparent",
                  boxShadow: "none",
                }}
              >
                <div className="flex flex-col rounded-[20px] bg-white px-2 py-3 text-sm font-semibold text-darkGrey-700 shadow-lg">
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={(e) => handleDownloadClick(e, "pdf")}
                  >
                    <HiDownload
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    Pdf
                  </button>
                  <button
                    className="flex rounded-[10px] px-3 py-2 hover:bg-darkGrey-100"
                    onClick={(e) => handleDownloadClick(e, "png")}
                  >
                    <HiDownload
                      className="me-2 text-secondaryGrey-900"
                      size={20}
                    />
                    Png
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {support && !isLoading ? (
        <>
          <div className="flex w-full flex-col space-y-6 md:space-y-8">
            <VisualSupportBanner
              support={support}
              handleFavoriteClick={handleFavoriteClick}
              isFavorite={!!favoriteSupport?.id}
              handleDownloadClick={handleDownloadClick}
            />
            <div className="flex flex-col space-x-0 space-y-6 md:flex-row md:space-x-8 md:space-y-0">
              <div className="flex w-full flex-col space-y-6 md:space-y-8">
                <div className="rounded-[30px] bg-white p-4 py-6 md:p-6">
                  <div className="flex w-full justify-center overflow-hidden md:py-12">
                    <img
                      src={`${support?.png}?timestamp=${Date.now()}`}
                      alt={support?.title}
                      className="h-full w-full max-w-[800px] object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Associated Techniques */}
              <div className="flex flex-col space-y-6 md:w-80 md:space-y-8">
                <div className="space-y-3 rounded-[30px] bg-white p-6">
                  <HiLink size={46} color="#47548C" />
                  <p className="text-xl font-bold text-secondaryGrey-900">
                    {translate(
                      `ns1:VisualSupport.${
                        support?.techniques.length > 1
                          ? "AssociatedTechniques"
                          : "AssociatedTechnique"
                      }`
                    )}
                  </p>
                  <ul className="text-sm font-bold font-medium text-darkGrey-700 underline">
                    {support?.techniques.map((technique, index) => (
                      <li key={"tech" + index}>
                        <Link to={`/techniques?id=${technique?.id}`}>
                          {technique.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                {support?.conseil && <Advice advice={support.conseil} />}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <div className="loader" />
        </div>
      )}
    </div>
  );
}
